import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import LazyTable, {
  BasicCellType,
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  Course,
  DeactivationMode,
  RusticiCourseSearchRequest,
  RusticiCourseSearchResult,
  RusticiDeactivationRequest,
  RusticiDeactivationScopeKind,
  RusticiScopeInfo,
  RusticiScopeRejection,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { dateToStandardFormat } from "utils/dateUtils";
import translateError from "utils/translateError";

enum PageSteps {
  Input = "input",
  Fetch = "fetch",
}

// ConfirmDialog routes escape and backdrop clicks through onCancel, so passing a no-op while the
// deactivation runs is what keeps it open.
const doNothing = () => {};

const rejectionMessages: Record<string, string> = {
  [RusticiScopeRejection.OrganizationAboveScormRoot]:
    "rusticiOrganizationAboveScormRoot",
  [RusticiScopeRejection.OrganizationNotLinkedToContentController]:
    "rusticiOrganizationNotLinked",
  [RusticiScopeRejection.ContentControllerAccountNotFound]:
    "rusticiContentControllerAccountNotFound",
  [RusticiScopeRejection.OrganizationHasAmbiguousContentControllerAccounts]:
    "rusticiOrganizationHasAmbiguousAccounts",
};

const modeOptions = [
  { value: DeactivationMode.SelfStudy, labelKey: "rusticiModeSelfStudy" },
  { value: DeactivationMode.Webinars, labelKey: "rusticiModeWebinars" },
  { value: DeactivationMode.Custom, labelKey: "rusticiModeCustom" },
];

const styles = {
  firstStep: {
    display: "flex",
  },
  textAreaWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "300px",
    marginRight: "20px",
    marginLeft: "30px",
  },
  fetchButtonWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  deactivateButton: {
    float: "right",
  },
  scopeAlert: {
    margin: "10px 0 20px 0",
  },
  confirmationTarget: {
    marginTop: "16px",
  },
};

const DeactivateRusticiCourses = () => {
  const { formatMessage: t } = useIntl();
  const { rusticiCourseService } = useGlobalContext();
  const queryClient = useQueryClient();
  const { organizationId, organizationName } = contextStore.useState();

  const [mode, setMode] = useState(DeactivationMode.Custom);
  const [textareaValue, setTextAreaValue] = useState("");
  const [skusFilter, setSkusFilter] = useState<string[]>([]);
  const [step, setStep] = useState(PageSteps.Input);
  const [deactivating, setDeactivating] = useState(false);

  const [deactivateConfirmOpen, deactivateConfirmOpenActions] = useBoolean(
    false
  );

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useTitle(t({ id: "deactivateRusticiCoursesPageTitle" }));

  // The organization is not a request parameter: the server reads it from the organization header this app
  // already sends. It stays in the query key so switching organization does not reuse a cached scope.
  const scopeResult = useQuery<RusticiScopeInfo>(
    ["rustici-scope", organizationId],
    () => rusticiCourseService.resolveScope(),
    { enabled: !!organizationId }
  );

  const resolvedScope = scopeResult?.data;

  const isCustomMode = mode === DeactivationMode.Custom;
  const hasRequiredInput = !isCustomMode || skusFilter.length > 0;
  const canFetch =
    !!organizationId &&
    resolvedScope?.kind !== RusticiDeactivationScopeKind.Rejected &&
    !scopeResult.isLoading &&
    !scopeResult.isError &&
    hasRequiredInput;

  const searchResult = useQuery<RusticiCourseSearchResult>(
    ["rustici-courses", organizationId, mode, skusFilter],
    () =>
      rusticiCourseService.findCoursesToDeactivate(
        new RusticiCourseSearchRequest({
          mode,
          skus: skusFilter,
        })
      ),
    // A preview of what is about to be destroyed must never be served from cache.
    { enabled: step === PageSteps.Fetch && canFetch, refetchOnMount: "always" }
  );

  // The search re-resolves the scope and can reject for reasons the scope call never sees, such as Content
  // Controller not recognising the account, so its scope is the one describing the courses on screen.
  const scope = searchResult?.data?.scope ?? resolvedScope;
  const isRejected = scope?.kind === RusticiDeactivationScopeKind.Rejected;
  const isContentLevel = scope?.kind === RusticiDeactivationScopeKind.Content;
  const rejectionMessageKey = scope?.rejection
    ? rejectionMessages[scope.rejection]
    : undefined;

  // The search error is scoped to the results step because it stays in the query cache after going back,
  // and would otherwise sit on the input form describing a request that is no longer on screen.
  const requestError =
    scopeResult.error ??
    (step === PageSteps.Fetch ? searchResult.error : null);

  // The dialog names the server-resolved organization, which is the one the deactivation runs against; the
  // page header keeps the breadcrumb name because that is what AC-5 asks for.
  const confirmationOrganizationName = scope?.organizationName ?? organizationName;

  const courses: Course[] = searchResult?.data?.courses ?? [];
  const coursesTableContent = courses
    .map((x) => ({
      ...x,
      id: x.id!,
      date: x.updated_date ? dateToStandardFormat(x.updated_date) : null,
    }))
    .slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => {
    setSkusFilter(
      textareaValue
        ?.split("\n")
        .map((x) => x.trim())
        .filter((x) => x.length > 0) ?? []
    );
  }, [textareaValue]);

  const backToInput = () => {
    setStep(PageSteps.Input);
    setPage(0);
  };

  // A preview belongs to the organization it was fetched for, so switching the breadcrumbs must never
  // leave a list on screen that could then be confirmed against a different organization.
  useEffect(() => {
    setStep(PageSteps.Input);
    setPage(0);
  }, [organizationId]);

  const deactivateCourses = async () => {
    try {
      setDeactivating(true);

      const summary = await rusticiCourseService.deactivateCourses(
        new RusticiDeactivationRequest({
          mode,
          skus: skusFilter,
          courseIds: courses.map((x) => x.id!),
        })
      );

      // A rejection comes back as a rejected summary rather than an error, and reporting it as
      // "0 deactivated" would read as a successful run that found nothing.
      if (summary.scope?.kind === RusticiDeactivationScopeKind.Rejected) {
        const rejectionKey = summary.scope.rejection
          ? rejectionMessages[summary.scope.rejection]
          : undefined;

        setSnackbarMessage(
          new SnackbarMessageProps(
            rejectionKey
              ? t(
                  { id: rejectionKey },
                  { detail: summary.scope.rejectionDetail ?? "" }
                )
              : t({ id: "serverError" }),
            Severity.Error
          )
        );

        return;
      }

      queryClient.invalidateQueries("rustici-courses");
      setTextAreaValue("");
      setSkusFilter([]);
      setStep(PageSteps.Input);
      setPage(0);
      setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
      const failed = summary.failedCourseIds?.length ?? 0;
      const skipped = summary.skippedCourseIds?.length ?? 0;

      const sentences = [
        t(
          { id: "rusticiDeactivationSucceeded" },
          { succeeded: summary.succeeded ?? 0 }
        ),
      ];

      if (failed > 0) {
        sentences.push(t({ id: "rusticiDeactivationFailed" }, { failed }));
      }

      if (skipped > 0) {
        sentences.push(t({ id: "rusticiDeactivationSkipped" }, { skipped }));
      }

      setSnackbarMessage(
        new SnackbarMessageProps(
          sentences.join(" "),
          failed > 0 ? Severity.Warning : Severity.Success
        )
      );
    } catch (error) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error)
      );
    } finally {
      setDeactivating(false);
      deactivateConfirmOpenActions.setFalse();
    }
  };

  const basicCell = {
    align: "left",
    width: 0,
    type: "text",
    noSort: true,
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...basicCell,
      accessor: "id",
      label: t({ id: "id" }),
    },
    {
      ...basicCell,
      accessor: "name",
      label: t({ id: "nameLabel" }),
    },
    {
      ...basicCell,
      accessor: "enabled",
      type: "boolean",
      label: t({ id: "enabledLabel" }),
    },
    {
      ...basicCell,
      accessor: "date",
      label: t({ id: "updatedDateLabel" }),
    },
  ];

  // The deactivation runs one course at a time and takes minutes, so the dialog stops asking and reports
  // instead: there is no AbortSignal, so dismissing it would let the work run on unseen.
  const confirmationContent = deactivating ? (
    <Alert severity="info">
      <T id="rusticiDeactivationInProgress" />
    </Alert>
  ) : (
    <>
      {isContentLevel ? (
        <Alert severity="warning">
          <T
            id="confirmDeactivationAllOrganizations"
            values={{ organizationName: confirmationOrganizationName }}
          />
        </Alert>
      ) : (
        <T
          id="confirmDeactivation"
          values={{ organizationName: confirmationOrganizationName }}
        />
      )}
      <Typography variant="body2" sx={styles.confirmationTarget}>
        <T
          id="rusticiDeactivationTarget"
          values={{
            courseCount: courses.length,
            host: scope?.contentControllerHost ?? "",
          }}
        />
      </Typography>
    </>
  );

  return (
    <Box>
      {step === PageSteps.Fetch && (
        <Button startIcon={<ArrowBackIcon />} onClick={backToInput}>
          <T id="backLabel" />
        </Button>
      )}
      <h1>
        <b>
          <T id="deactivateRusticiCoursesPageTitle" />
        </b>
      </h1>
      <h2>
        <T id="rusticiOrganizationHeader" values={{ organizationName }} />
      </h2>
      {/* Rejected is the default scope kind and None the default rejection, so a rejection this page has no
          key for would otherwise hide the results and render nothing at all. */}
      {isRejected && (
        <Alert severity="warning" sx={styles.scopeAlert}>
          <T
            id={rejectionMessageKey ?? "serverError"}
            values={{ detail: scope?.rejectionDetail ?? "" }}
          />
        </Alert>
      )}
      {!!requestError && (
        <Alert severity="error" sx={styles.scopeAlert}>
          {translateError(requestError, t)}
        </Alert>
      )}
      {step === PageSteps.Input && (
        <Box>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as DeactivationMode)}
          >
            {modeOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={t({ id: option.labelKey })}
                disabled={isRejected}
              />
            ))}
          </RadioGroup>
          <Box sx={styles.firstStep}>
            {isCustomMode && (
              <Box sx={styles.textAreaWrapper}>
                <Typography>
                  <T id="addCoursesToDeactivate" />
                </Typography>
                <TextareaAutosize
                  minRows={30}
                  maxRows={30}
                  value={textareaValue}
                  onChange={(e) => setTextAreaValue(e.target.value)}
                  disabled={isRejected}
                />
              </Box>
            )}
            <Box sx={styles.fetchButtonWrapper}>
              <Button
                color="primary"
                variant="outlined"
                disabled={!canFetch}
                onClick={() => setStep(PageSteps.Fetch)}
              >
                <T id="fetchCoursesFromRusticiCC" />
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {step === PageSteps.Fetch && !isRejected && !searchResult.isError && (
        <Box>
          {/* isFetching, not isLoading: isLoading is false while a refetch serves cached rows, which would
              let a stale preview be confirmed. */}
          <Button
            color="primary"
            variant="contained"
            sx={styles.deactivateButton}
            disabled={deactivating || searchResult.isFetching || !courses.length}
            onClick={deactivateConfirmOpenActions.setTrue}
          >
            <T id="deactivateLabel" />
          </Button>
          <ConfirmDialog
            dialogTitleKey="deactivateLabel"
            rawContent={confirmationContent}
            confirmLabel="confirmButton"
            open={deactivateConfirmOpen}
            onCancel={
              deactivating ? doNothing : deactivateConfirmOpenActions.setFalse
            }
            handleConfirm={deactivateCourses}
          />
          <LazyTable
            {...{
              rowCount: courses.length,
              rows: coursesTableContent,
              loading: searchResult.isFetching,
              dense: true,
              title: t({ id: "coursesFoundInRusticiCC" }),
              cells,
              page,
              setPage,
              order: "asc",
              orderBy: "",
              setOrder: () => {},
              setOrderBy: () => {},
              rowsPerPage,
              setRowsPerPage,
            }}
          />
        </Box>
      )}
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default DeactivateRusticiCourses;
